import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useCallback, useContext } from 'react';

import { CookiePreferencesBannerContext } from '@/components/app/layout/CookiePreferencesBannerProvider';
import Layout from '@/components/layout/LayoutWrapper';
import EnPrivacyPolicyMDX from '@/markdown/terms/privacy-policy/en.mdx';
import { Locale } from '@/store/locale/types';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

interface PrivacyProps {
	meta: {
		title: string;
	};
	initialState: { locale: Locale };
}

interface LocaleMdxProps {
	onOptOutClick: () => void;
}

/*
 * Need to use switch statement due to need for static strings in `import` function within `dynamic()`
 * https://nextjs.org/docs/advanced-features/dynamic-import
 * This might eventually get fixed: https://github.com/vercel/next.js/discussions/33980
 */
const localeMdx = (locale: Locale = 'en') => {
	switch (locale) {
		case 'fr':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/fr.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'ja':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/ja.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'es':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/es.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'de':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/de.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'pt':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/pt.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'ko':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/ko.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'it':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/it.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'pl':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/pl.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		case 'zh':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/privacy-policy/zh.mdx').then(mdx => mdx).catch(() => EnPrivacyPolicyMDX),
			);
		// English fallback
		case 'en':
		default:
			return EnPrivacyPolicyMDX;
	}
};

const Privacy = ({ initialState: { locale } }: PrivacyProps) => {
	const LocaleMdx = localeMdx(locale);
	const { setCanBeShown, setIsAnnounced, setIsForciblyShown } = useContext(CookiePreferencesBannerContext);
	const openCookiesBanner = useCallback(() => {
		setCanBeShown(true);
		setIsAnnounced(true);
		setIsForciblyShown(true);
	}, [setIsAnnounced, setIsForciblyShown, setCanBeShown]);

	// TODO: refactor this code to follow best practices
	/* eslint-disable react-hooks/static-components */
	return (
		<Layout pageName="privacy-policy" title="Calm - Privacy Policy">
			<LocaleMdx onOptOutClick={openCookiesBanner} />
		</Layout>
	);
	/* eslint-enable react-hooks/static-components */
};

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState,
			hideAcceptCookiesModal: true,
		},
	};
};

export default Privacy;
